/**
 * User Service
 *
 * User profile API methods.
 */

import type { EvidenceRetentionNotice } from '@tonnie/foundation';
import { TonnieClient } from '../client/base';
import { API_ENDPOINTS } from '../client/config';
// ── F50 region ──
import type {
  IdentityDocumentApplyField,
  IdentityDocumentConfirmResult,
  IdentityDocumentReview,
  NotificationChannelMatrix,
  NotificationChannelMatrixPatch,
  ProfileRequiredField,
  ProfileStrength,
} from '@tonnie/types';
import type { CitizenSetupInput, IdentityDocumentReadInput } from '@tonnie/schemas';
// ── end F50 region ──

export interface TalentProfile {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  /** The legal name, as printed on the last confirmed passport / ID card. */
  legalFullName?: string | null;
  dateOfBirth?: string | null;
  /** The first citizenship (canonical column) — kept for the readers that need one. */
  nationality?: string;
  /**
   * Every citizenship, canonical first, resolved by the server — the one list
   * to show. Never re-derive it from `customFields`.
   */
  citizenships: string[];
  currentCountry?: string;
  currentCity?: string;
  jobTitle?: string;
  seniorityLevel?: string;
  skills?: Record<string, unknown>;
  languages?: Record<string, unknown>[];
  linkedinUrl?: string;
  pipelineStage?: string;
  source?: string;
  customFields?: Record<string, unknown>;
}

export interface UserProfile {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName: string;
  avatarUrl?: string | null;
  imageUrl?: string;
  /**
   * Canonical photo contract (BL-009) — computed server-side in ONE place.
   * Render `photo.url`. Everyone may change their own photo (DW6-5).
   */
  photo?: {
    url: string | null;
    source: 'upload' | 'oauth' | 'linkedin' | null;
  };
  /**
   * Language levels resolved server-side from the talent's stored
   * languages, keyed by ISO 639-1 (BL-013). englishLevel/portugueseLevel
   * are convenience projections of the same map.
   */
  languageLevels?: Record<string, string>;
  englishLevel?: string;
  portugueseLevel?: string;
  phone?: string;
  dateOfBirth?: string;
  gender?: string;
  country?: string;
  timezone?: string;
  language: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  onboardingCompleted: boolean;
  settings: UserSettings;
  talent?: TalentProfile | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserSettings {
  notifications: {
    pushEnabled: boolean;
    emailEnabled: boolean;
    smsEnabled: boolean;
    quietHours?: {
      enabled: boolean;
      start: string;
      end: string;
    };
    categories?: {
      hero: boolean;
      system: boolean;
      social: boolean;
      alert: boolean;
    };
    // ── F50 region: email × push per category, as it decides delivery ──
    matrix?: NotificationChannelMatrix;
    // ── end F50 region ──
  };
  privacy: {
    /** "Share Analytics" — when false nothing is captured (the web applies it). */
    shareAnalytics: boolean;
    showInTalentPool: boolean;
  };
  accessibility?: {
    reducedMotion: boolean;
    highContrast: boolean;
  };
  /**
   * Flexible app-scoped preferences stored in the users.settings JSONB.
   * Each app namespaces its own keys (e.g. `preferences.business` for
   * business-web) so sibling apps can coexist without collisions.
   */
  preferences?: Record<string, unknown>;
}

export interface UpdateProfileInput {
  firstName?: string;
  lastName?: string;
  phone?: string;
  dateOfBirth?: string;
  gender?: string;
  country?: string;
  timezone?: string;
  /** ISO locale code, e.g. "en", "pt". Maps to users.locale in the DB. */
  locale?: string;
  /** @deprecated use `locale` — backend ignores this field. */
  language?: string;
}

export interface CompleteOnboardingInput {
  firstName?: string;
  lastName?: string;
  phone?: string;
  dateOfBirth?: string;
  nationalities?: string[];
  currentCountry?: string;
  /** `talents.current_city` — one of the six fields the CheckUp score needs. */
  currentCity?: string;
  targetCountry?: string;
  goal?: string;
  role?: string;
  seniority?: string;
  englishLevel?: string;
  skills?: Record<string, unknown>;
  education?: Record<string, unknown>[];
  experience?: Record<string, unknown>[];
  languages?: Record<string, unknown>[];
  familyConfig?: Record<string, unknown>;
  mobility?: Record<string, unknown>;
  onboardingMode?: string;
  workModelPreference?: string[];
  address?: Record<string, unknown>;
  websiteUrls?: Record<string, unknown>[];
  phoneCountryCode?: string;
  avatarUrl?: string;
}

export interface UpdateSettingsInput {
  notifications?: {
    pushEnabled?: boolean;
    emailEnabled?: boolean;
    smsEnabled?: boolean;
    quietHours?: Partial<NonNullable<UserSettings['notifications']['quietHours']>>;
    categories?: Partial<NonNullable<UserSettings['notifications']['categories']>>;
    // ── F50 region: only the cells being changed; the server merges ──
    matrix?: NotificationChannelMatrixPatch;
    // ── end F50 region ──
  };
  privacy?: Partial<UserSettings['privacy']>;
  accessibility?: Partial<NonNullable<UserSettings['accessibility']>>;
  /**
   * Flexible app-scoped preferences. Merged deep into the stored JSONB so
   * apps can update their own namespace without clobbering siblings.
   */
  preferences?: Record<string, unknown>;
}

/** The six fields F33 requires — the one contract lives in `@tonnie/types`. */
export type { ProfileRequiredField } from '@tonnie/types';

export interface ProfileCompleteness {
  /** `completed ÷ required`, rounded. Rendered verbatim — never recomputed. */
  percent: number;
  complete: boolean;
  /** Every required field, in the order the client should render them. */
  requiredFields: Array<{ key: ProfileRequiredField; complete: boolean }>;
  missingFields: ProfileRequiredField[];
  /** The whole profile, item by item — decided on the server, rendered verbatim. */
  strength: ProfileStrength;
}

/** `POST /users/me/setup` — the journeys the answer just opened. */
export interface CitizenSetupResult {
  success: true;
  setupCompleted: true;
  activatedJourneyIds: string[];
}

export class UserService {
  constructor(private readonly client: TonnieClient) {}

  /**
   * Get current user profile
   */
  async getProfile(): Promise<UserProfile> {
    return this.client.get<UserProfile>(API_ENDPOINTS.USERS.ME);
  }

  /**
   * Update user profile
   */
  async updateProfile(input: UpdateProfileInput): Promise<UserProfile> {
    return this.client.patch<UserProfile>(API_ENDPOINTS.USERS.ME, input);
  }

  /**
   * Get user settings
   */
  async getSettings(): Promise<UserSettings> {
    return this.client.get<UserSettings>(`${API_ENDPOINTS.USERS.ME}/settings`);
  }

  /**
   * Update user settings
   */
  async updateSettings(input: UpdateSettingsInput): Promise<UserSettings> {
    return this.client.patch<UserSettings>(`${API_ENDPOINTS.USERS.ME}/settings`, input);
  }

  /**
   * Upload avatar image to S3 via the API.
   * Returns the presigned URL persisted for display.
   *
   * Accepts a web `File` (browser) OR a React-Native multipart descriptor
   * (`{ uri, name, type }`) — RN's `FormData` resolves the descriptor to the
   * actual file bytes. Same dual-shape pattern as `feedback.service.ts`.
   */
  async uploadAvatar(
    file: File | { uri: string; name: string; type: string }
  ): Promise<{ avatarUrl: string }> {
    const formData = new FormData();
    formData.append('file', file as Blob);
    return this.client.postForm<{ avatarUrl: string }>(
      `${API_ENDPOINTS.USERS.ME}/avatar`,
      formData
    );
  }

  /**
   * Remove the user's avatar.
   * Clears `users.avatar_url` server-side and best-effort removes the
   * underlying S3 object. Idempotent — safe to call when no avatar is set.
   */
  async deleteAvatar(): Promise<{ success: boolean }> {
    return this.client.delete<{ success: boolean }>(`${API_ENDPOINTS.USERS.ME}/avatar`);
  }

  /**
   * Upload profile image (legacy alias for uploadAvatar).
   */
  async uploadProfileImage(file: File): Promise<{ imageUrl: string }> {
    const formData = new FormData();
    formData.append('image', file);
    return this.client.postForm<{ imageUrl: string }>(`${API_ENDPOINTS.USERS.ME}/image`, formData);
  }

  /**
   * Delete user account.
   *
   * `evidenceRetention` (W5.8 · Art. 12(4)) lists every company that keeps a
   * decision record about the citizen after the deletion — which company,
   * what, until when and why. Empty means none does.
   */
  async deleteAccount(): Promise<{
    success: true;
    evidenceRetention: EvidenceRetentionNotice[];
  }> {
    return this.client.delete<{ success: true; evidenceRetention: EvidenceRetentionNotice[] }>(
      API_ENDPOINTS.USERS.ME
    );
  }

  /**
   * Global onboarding-status snapshot: is this citizen set up, and which talent
   * row is their canonical profile (migration 0042).
   *
   * `setupComplete` is the one answer every client gates on, decided by the
   * server: "onboarding stamped AND origin known" — the same two facts the
   * server needs to activate a waiting journey.
   */
  async getOnboardingStatus(): Promise<{
    setupComplete: boolean;
    primaryTalentId: string | null;
    completedAt: string | null;
  }> {
    return this.client.get(API_ENDPOINTS.USERS.ME_ONBOARDING_STATUS);
  }

  /**
   * How complete is my profile, and what is missing? (W8.1 · F33)
   *
   * `percent` is computed by the SERVER as `completed ÷ required`. The client
   * renders it and never recomputes: the A2 mockup showed 60 % for 3-of-6 and
   * 80 % for 4-of-6, neither of which that arithmetic produces, and making the
   * server the only place the number exists is what stops the design and the
   * code disagreeing. It is also the no-business-logic-in-UI rule — the client
   * does not know what the six required fields are.
   */
  async getProfileCompleteness(): Promise<ProfileCompleteness> {
    return this.client.get<ProfileCompleteness>(API_ENDPOINTS.USERS.ME_PROFILE_COMPLETENESS);
  }

  /** Read the uploaded passport / ID card; the profile is not touched. */
  async readIdentityDocument(input: IdentityDocumentReadInput): Promise<IdentityDocumentReview> {
    return this.client.post<IdentityDocumentReview>(
      API_ENDPOINTS.USERS.ME_IDENTITY_DOCUMENTS,
      input
    );
  }

  /** Write the ticked lines of the review to the profile. */
  async confirmIdentityDocument(
    id: string,
    apply: IdentityDocumentApplyField[]
  ): Promise<IdentityDocumentConfirmResult> {
    return this.client.post<IdentityDocumentConfirmResult>(
      API_ENDPOINTS.USERS.ME_IDENTITY_DOCUMENT_CONFIRM(id),
      { apply }
    );
  }

  /** Discard a document not yet confirmed, and its Vault files. */
  async discardIdentityDocument(id: string): Promise<void> {
    await this.client.delete<void>(API_ENDPOINTS.USERS.ME_IDENTITY_DOCUMENT(id));
  }

  /**
   * Save onboarding progress without marking as complete.
   * Called on each step transition to persist partial data.
   */
  async saveOnboardingProgress(input: CompleteOnboardingInput): Promise<{ success: boolean }> {
    return this.client.patch<{ success: boolean }>(
      `${API_ENDPOINTS.USERS.ME}/onboarding/progress`,
      input
    );
  }

  /**
   * The citizen's one-screen setup: the country they are leaving and their
   * city. Stamps onboarding complete and activates every journey waiting on an
   * origin.
   */
  async completeSetup(input: CitizenSetupInput): Promise<CitizenSetupResult> {
    return this.client.post<CitizenSetupResult>(API_ENDPOINTS.USERS.ME_SETUP, input);
  }

  /**
   * Complete onboarding with full payload
   */
  async completeOnboarding(input?: CompleteOnboardingInput): Promise<void> {
    return this.client.post<void>(`${API_ENDPOINTS.USERS.ME}/onboarding/complete`, input ?? {});
  }
}

export function createUserService(client: TonnieClient): UserService {
  return new UserService(client);
}
